// helpers
import { getBridgeSdk } from "@swiggy-private/checkout-core";
import { shouldMenuRefresh } from "@cart-client/helpers/Cart";

// types
import { NativeLocalCart } from "@swiggy-private/cart-types";
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// hooks
import { useLocalCartTransformer } from "@cart-client/hooks/useLocalCartTransformer";

export interface CartState {
    uniqueIdForCart: string;
    localCart: NativeLocalCart | null;
    reviewedCart: ReviewedCart | null;
}

export enum CartActionTypes {
    UPDATE_LOCAL_CART,
    UPDATE_REVIEWED_CART,
    UPDATE_CART,
    CLEAR_LOCAL_CART,
}

export type CartAction =
    | { type: CartActionTypes.UPDATE_LOCAL_CART; payload: NativeLocalCart }
    | { type: CartActionTypes.UPDATE_REVIEWED_CART; payload: ReviewedCart }
    | {
          type: CartActionTypes.UPDATE_CART;
          payload: {
              uniqueIdForCart: string;
              localCart: NativeLocalCart | null;
              reviewedCart: ReviewedCart;
          };
      }
    | { type: CartActionTypes.CLEAR_LOCAL_CART; payload: { reviewedCart: ReviewedCart } };

export function CartReducer(state: CartState, action: CartAction): CartState {
    const { transformLocalCart } = useLocalCartTransformer();
    const { localCart, reviewedCart } = state || {};
    const { type, payload } = action || {};

    switch (type) {
        case CartActionTypes.UPDATE_LOCAL_CART: {
            const transformedLocalCart = transformLocalCart({ ...localCart, ...payload }, reviewedCart);

            const updatedCart = {
                ...state,
                localCart: transformedLocalCart,
            };

            getBridgeSdk().updateLocalCart({
                tenant: {
                    marketplaceBusinessline: "Regular",
                    marketplaceCategory: "Food",
                    marketplaceId: 1,
                },
                data: {
                    ...updatedCart.localCart,
                    shouldRefreshMenu: shouldMenuRefresh(reviewedCart?.statusCode),
                },
            });
            return updatedCart;
        }
        case CartActionTypes.UPDATE_REVIEWED_CART:
            return Object.assign({}, state, { reviewedCart: payload });
        case CartActionTypes.UPDATE_CART: {
            const transformedLocalCart = transformLocalCart(
                {
                    ...localCart,
                    ...(payload as {
                        localCart: NativeLocalCart | null;
                        reviewedCart: ReviewedCart;
                    }).localCart,
                },
                (payload as {
                    localCart: NativeLocalCart | null;
                    reviewedCart: ReviewedCart;
                }).reviewedCart,
            );

            const isPrefetchXpEnabled = typeof window !== "undefined" && window.__headers__?.cartpageprefetch === "true";

            // eslint-disable-next-line @typescript-eslint/naming-convention
            let cartResponse: { last_updated_timestamp: string; response: Record<string, unknown> } | undefined;
            if (isPrefetchXpEnabled) {
                // Use original prefetch timestamp for initial load (preserves TTL clock),
                // Date.now() for real API responses (background refresh, mutations)
                const prefetchTs = window.__prefetchTimestamp__;
                const timestamp = prefetchTs || String(Date.now());
                // Clear after first use — subsequent UPDATE_CART dispatches use Date.now()
                if (prefetchTs) {
                    window.__prefetchTimestamp__ = undefined;
                }
                cartResponse = {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    last_updated_timestamp: timestamp,
                    response: (payload as { reviewedCart: ReviewedCart }).reviewedCart as unknown as Record<string, unknown>,
                };
            }

            const updateLocalCartPayload = {
                tenant: {
                    marketplaceBusinessline: "Regular",
                    marketplaceCategory: "Food",
                    marketplaceId: 1,
                },
                // eslint-disable-next-line @typescript-eslint/naming-convention
                ...(isPrefetchXpEnabled && { cart_response: cartResponse }),
                data: {
                    ...transformedLocalCart,
                    shouldRefreshMenu: shouldMenuRefresh(
                        (action?.payload as {
                            localCart: NativeLocalCart | null;
                            reviewedCart: ReviewedCart;
                        })?.reviewedCart?.statusCode,
                    ),
                },
            };

            getBridgeSdk().updateLocalCart(updateLocalCartPayload);

            return {
                ...state,
                localCart: transformedLocalCart,
                reviewedCart: (payload as {
                    reviewedCart: ReviewedCart;
                }).reviewedCart,
                uniqueIdForCart: (payload as {
                    uniqueIdForCart: string;
                }).uniqueIdForCart,
            };
        }
        case CartActionTypes.CLEAR_LOCAL_CART:
            return {
                ...state,
                localCart: null,
                reviewedCart: {
                    ...reviewedCart,
                    ...(payload as {
                        reviewedCart: ReviewedCart;
                    }).reviewedCart,
                },
            };
        default:
            throw new Error(`unhandled action type ${type}`);
    }
}
